<template>
  <div v-if="!loading" class="flex flex-col">
    <SimpleBreadCrumb class="md:px-8" custom-active-class="font-semibold" />
    <!-- Mobile layout for the new excursion configurator design: orders the chrome as
         Back → Title → Save for later → Stepper → Order Summary → step content → Continue. -->
    <div v-if="showNewMobileConfiguratorLayout" class="flex flex-col h-full bg-gray-100">
      <div
        v-if="selectedSessionId"
        class="px-6 py-4"
        :class="[secondaryColor ? `bg-gray-275` : `bg-teal-light`]"
      >
        <h3 class="text-base font-montserrat text-indigo-base">
          {{ configuratorLocation }}
        </h3>
        <h2 class="text-2xl font-bold text-indigo-base">
          {{ configuratorProgramName }}
        </h2>
        <SaveForLaterButton
          :disabled="disabledSaveForLater"
          :error="saveForLaterError"
          @save="saveForLater"
        />
      </div>
      <stepped-process
        :steps="steps"
        :current-step="currentStep"
        class="w-full bg-white"
        @set-current-step="(slug) => setCurrentStep(slug)"
      >
        <template #after-steps>
          <div
            v-if="selectedSessionId"
            data-testid="order-details-background"
            class="px-6 py-4"
            :class="[secondaryColor ? `bg-gray-275` : `bg-teal-light`]"
          >
            <order-details
              :current-order="unsavedOrder"
              :current-step="currentStep"
              :ope8-id="ope8Id"
              :home-institution-id="homeInstitutionId"
              :hide-prices="hidePrices"
              :hide-summary-header="true"
              @get-pricing="(_pricing) => (pricing = _pricing)"
            />
          </div>
        </template>
      </stepped-process>
    </div>
    <div v-else class="flex flex-col justify-start h-full bg-gray-100 md:block md:px-12">
      <stepped-process
        :steps="steps"
        :current-step="currentStep"
        class="inline-block w-full h-full align-top bg-white md:w-9/12"
        @set-current-step="(slug) => setCurrentStep(slug)"
      />
      <div
        v-if="selectedSessionId"
        data-testid="order-details-background"
        class="block order-first px-6 py-4 h-full md:w-1/4 md:leading-6 md:inline-block md:sticky md:align-top md:top-0"
        :class="[secondaryColor ? `bg-gray-275` : `bg-teal-light`]"
      >
        <order-details
          :current-order="unsavedOrder"
          :current-step="currentStep"
          :ope8-id="ope8Id"
          :home-institution-id="homeInstitutionId"
          :hide-prices="hidePrices"
          @get-pricing="(_pricing) => (pricing = _pricing)"
        >
          <template #call-to-action>
            <SaveForLaterButton
              :disabled="disabledSaveForLater"
              :error="saveForLaterError"
              @save="saveForLater"
            />
          </template>
        </order-details>
      </div>
    </div>
  </div>
  <template v-else>
    <Spinner />
  </template>
  <ApplicationExistsModal
    :show-application-taken-modal="showApplicationTakenModal"
    :redirect="true"
    @close="showApplicationTakenModal = false"
  />
</template>

<script setup>
import _ from "lodash";
import { validate as validateUUID } from "uuid";
import {
  computed,
  inject,
  onMounted,
  provide,
  reactive,
  readonly,
  ref,
  toRefs,
  toValue,
  watch,
  watchEffect,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import ApplicationExistsModal from "@/components/ExperiencePage/ApplicationExistsModal.vue";
import { applicationAlreadyExists, getInternshipSectionTitle } from "@/components/ExperiencePage/utils.js";
import Spinner from "@/components/helpers/Spinner.vue";
import OrderDetails from "@/components/OrderDetails/OrderDetails.vue";
import { TRANSCRIPT_SERVICE_IDS } from "@/components/program-manager/sessions/constants";
import SteppedProcess from "@/components/SteppedProcess/SteppedProcess.vue";
import SimpleBreadCrumb from "@/components/shared/SimpleBreadCrumb.vue";
import {
  canRegisterForPrograms,
  hasMyStudentsToDosEnabled,
  showNewExcursionConfiguratorDesign,
} from "@/composables/authorization.js";
import { useMobile } from "@/composables/useMobile";
import { useNotifications } from "@/composables/useNotifications";
import { useProgramSelectionData } from "@/composables/useProgramSelectionData.js";
import {
  ERROR_TIMEOUT,
  requiredSchool as learnerTypeIdsThatRequireSchoolSelection,
  SALESFORCE_UMASS_ID,
  TODOS_IDS,
} from "@/constants";
import { ONE_CREDIT_PRODUCTS, THREE_CREDIT_PRODUCTS } from "@/constants.js";
import enrollmentService from "@/services/enrollment.js";
import entitiesService from "@/services/entities.js";
import internshipService from "@/services/internship";
import miscProductsService from "@/services/miscProducts.js";
import ordersService from "@/services/orders.js";
import { specialPricingOverrideEntityId, specialPricingOverrideParam } from "@/util/specialPricingOverride";
import { useConfigurator } from "../composables/useConfigurator";
import { useUrlState } from "../composables/useUrlState";
import { buildOrderQueryParams } from "../utils/utils";
import CoursesStep from "./CoursesStep.vue";
import CreditsStep from "./CreditsStep.vue";
import ExcursionConfigurator from "./Excursion/ExcursionConfigurator/index.vue";
import ExcursionsStep from "./ExcursionLegacy/ExcursionsStep.vue";
import HousingStep from "./Housing/HousingStep.vue";
import InternshipStep from "./Internship/InternshipStep.vue";
import ProgramStep from "./ProgramStep.vue";
import { ExcursionPrice, RoomPrice } from "./pricing";
import SaveForLaterButton from "./SaveForLaterButton.vue";
import StepsCompleted from "./StepsCompleted.vue";

const props = defineProps({
  // see useApplication.js for application definition
  application: {
    type: Object,
    default: () => {},
    required: true,
  },
  step: {
    type: String,
    default: "",
    requird: false,
  },
  stage: {
    type: String,
    default: "personalize",
    required: true,
  },
});

const store = useStore();
const toast = useToast();

const showApplicationTakenModal = ref(false);
const duplicateApplication = ref(false);
const pricing = ref(undefined);
const saveForLaterError = ref("");
const disabledSaveForLater = ref(false);

const { isMobile } = useMobile();

const showNewMobileConfiguratorLayout = computed(() => isMobile.value && showNewExcursionConfiguratorDesign.value);

const configuratorLocation = computed(() =>
  [pricing.value?.session_city, pricing.value?.session_country].filter(Boolean).join(", "),
);

const configuratorProgramName = computed(() => pricing.value?.program_name ?? pricing.value?.session_name ?? "");
const setLoadingMessage = inject("setRegistrationLoadingMessage", () => {});

const { application, step } = toRefs(props);

const { excursionRules, selectedExcursionIds, excursionsStepIsComplete, isLoading, hasErrors, errors } =
  useConfigurator({ application });

const _loading = ref(false);
const loading = computed(() => application.value?.isLoading?.value || _loading.value);
const router = useRouter();
const route = useRoute();
const orderApplicationId = computed(() => toValue(application.value?.applicationId));

const savedOrder = computed(() => toValue(application.value?.primaryOrder));
const savedHousingQuestionnaire = computed(() => toValue(application.value?.housingQuestionnaire));

const programPageId = computed(() => application.value?.programPageId?.value);
const programSelectionId = computed(() => application.value?.programSelectionSubmission?._id);

const applicationSessionId = computed(() => application.value?.sessionId?.value);

const { makeURLArrayState, updateURLState } = useUrlState();

const selectedSessionId = computed(() =>
  validateUUID(applicationSessionId.value) ? applicationSessionId.value : undefined,
);

const loadedSessions = computed(() => application.value?.programPageSessions?.value ?? []);

const selectedSession = computed(() => {
  if (loadedSessions.value.length === 0 || !selectedSessionId.value) {
    return undefined;
  }
  const session = loadedSessions.value.find((sess) => sess?.id === selectedSessionId.value);
  // this is hateful
  // I concur - MW
  const sessionUnits = session?.session_units ?? [];
  const sessionUnitsWithInstitutionSpecialPricingApplied = sessionUnits.map((sessionUnit) => {
    return {
      ...sessionUnit,
      unit: {
        ...sessionUnit.unit,
        rooms:
          sessionUnit?.unit?.rooms?.map((room) => {
            const productListing = configuratorOptions.value?.rooms?.find(
              (listing) => listing?.productId === room?.id,
            );
            const student_total_price_in_cents =
              productListing?.studentTotalPriceInCents ?? room?.student_total_price_in_cents ?? 0;

            return {
              ...room,
              student_total_price_in_cents,
            };
          }) ?? [],
      },
    };
  });

  return {
    ...session,
    session_units: sessionUnitsWithInstitutionSpecialPricingApplied,
  };
});

const numberCredits = computed({
  get: () => {
    const value = Number(route.query?.credits);
    return isNaN(value) ? 0 : value;
  },
  set: (value = 0) => {
    updateURLState("credits", value);
  },
});

const roomIds = makeURLArrayState("rooms");
const internshipIds = makeURLArrayState("internships");
const miscProductIds = makeURLArrayState("misc_products");

/**
 * Render cycles means that this does not always get the most up-to-date value
 * for homeInstitutionId.value
 *
 * @param homeSchoolId
 */
const fetchAndSetSelectedSchool = async (homeSchoolId) => {
  if (!homeSchoolId) {
    program.school = undefined;
    return;
  }

  const schoolFromLoadedUniversities = program.loadedUniversities.find((uni) => uni.id === homeSchoolId);
  if (schoolFromLoadedUniversities) {
    program.school = schoolFromLoadedUniversities;
    return;
  }

  entitiesService
    .getEntityById(homeSchoolId)
    .then(({ data }) => {
      program.school = data?.data?.items;
    })
    .catch(() => {
      program.school = undefined;
    });
};

const homeInstitutionId = computed({
  // TODO in getter, reject if not a valid id...  maybe?
  // Problem with that is  we'd have to round trip to Learning service
  get: () => {
    let homeInstitution = route.query?.homeInstitutionId;
    if (!homeInstitution) {
      homeInstitution = homeInstitutionIdFromProfile.value;
    }
    return homeInstitution;
  },
  set: (id) => {
    updateURLState("homeInstitutionId", id ?? "").then(() => fetchAndSetSelectedSchool(id));
  },
});

const defaultLearnerTypeId = computed(() => {
  // Sets "US College Student" by default OR the matching id from profile service
  return store.state?.profileData?.learner_type_id;
});

const learnerTypeId = computed({
  get: () => {
    const id = route.query?.learnerType;
    return validateUUID(id) ? id : defaultLearnerTypeId.value;
  },
  set: (id = "") => {
    updateURLState("learnerType", id).then(() => {
      if (!learnerTypeIdsThatRequireSchoolSelection.includes(id)) {
        homeInstitutionId.value = undefined;
      } else if (selectedSession.value?.allowed_participants?.length === 1) {
        homeInstitutionId.value = selectedSession.value?.allowed_participants?.[0]?.id;
      }
    });
  },
});

const homeInstitutionIdFromProfile = computed(() => {
  const affiliatedSchools = store.state?.profileData?.colleges ?? [];
  if (affiliatedSchools.length > 0) {
    return affiliatedSchools[0]?.college_id;
  }

  return undefined;
});

const _productsOptionsResponse = ref(undefined);
const configuratorOptions = computed(() => {
  const data = _productsOptionsResponse.value;

  const rooms =
    data?.rooms
      ?.map((room) => {
        return { ...RoomPrice }.optionMapper(room);
      })
      .toSorted((l, r) => l.isCheaper(r)) ?? [];

  const event_occurrences =
    data?.event_occurrences?.map((excursion) => {
      return { ...ExcursionPrice }.optionMapper(excursion);
    }) ?? [];

  return {
    isLoaded: Boolean(loadedSessions.value.length > 0 && data),
    rooms,
    internships: data?.internships ?? [],
    event_occurrences,
    addons: data?.addons ?? [],
    additional_fees: data?.additional_fees ?? [],
  };
});

const loadConfiguratorOptions = async () => {
  if (!selectedSessionId.value) {
    return;
  }

  const isHomeInstitutionType = learnerTypeIdsThatRequireSchoolSelection.includes(learnerTypeId.value);
  if (homeInstitutionId.value?.length > 0 || !isHomeInstitutionType) {
    const homeInstitution = isHomeInstitutionType ? { home_institution_id: homeInstitutionId.value } : {};
    await ordersService
      .getOrderOptionsPricing({
        payload: {
          session_id: selectedSessionId.value,
          rooms: [],
          internships: [],
          event_occurrences: [],
          addons: [],
          additional_fees: [],
          ...specialPricingOverrideParam(),
          ...homeInstitution,
        },
        params: {
          get_active: true,
        },
      })
      .then(
        (response) => {
          if (response.data) {
            _productsOptionsResponse.value = response.data;
          }
        },
        (error) => {
          const msg = error.response ? error.response.data?.message : error.message;
          console.error(`An error occurred: ${msg}`);
        },
      );
  }
};

const program = reactive({
  learnerType: undefined,
  school: undefined,
  loadedUniversities: [],
  name: undefined,
  city: undefined,
  country: undefined,
  programSession: undefined,
});

const learnerTypes = computed(() => store.getters["learnerTypes/getLearnerTypes"]);

/* watch learnerTypeId and update program.learnerType when it changes
 * this is needed b/c we need program.learnerType in vuex for the
 * account creation section after configurator
 */
watch(
  learnerTypeId,
  (newId, oldId) => {
    program.learnerType = learnerTypes.value.find((type) => type.id === newId);
    if (_.isEqual(newId, oldId)) {
      return;
    }
    loadConfiguratorOptions();
  },
  { immediate: true },
);

watchEffect(() => {
  program.programSession = selectedSession.value;
});

const ope8Id = computed(() => {
  const ope8IdIntegerValue = program?.school?.ope_id;
  if (!ope8IdIntegerValue) {
    return undefined;
  }
  return String(ope8IdIntegerValue).padStart(8, "0");
});

const currentUser = computed(() => store.getters.getCurrentUser);

const participantId = computed(() => currentUser.value?.participantId ?? "");

const housing = reactive({
  limit: 1, // max limit of rooms
  roomIds: readonly(roomIds),
  questionnaire: undefined,
  questionnaireId: undefined,
});

const internship = reactive({
  internshipIds: readonly(internshipIds.value),
  internships: [],
  maxItems: 3,
  requiredItems: 1,
  internshipItems: {
    items: [],
    count: 0,
  },
  loading: true,
});

const credits = reactive({
  option: "",
  cost: 0,
  miscId: "",
  prices: {},
  loading: false,
});

const setCreditsValue = (newProduct, oldProduct) => {
  // remove any previous selection from the list of add-ons
  let updatedMiscProductIds = miscProductIds.value.filter((id) => id !== oldProduct?.productId);

  // update for new selection
  credits.cost = newProduct?.cost;
  credits.option = newProduct?.value;
  credits.miscId = newProduct?.productId;

  if (newProduct?.productId && !updatedMiscProductIds.includes(newProduct.productId)) {
    updatedMiscProductIds = [...updatedMiscProductIds, newProduct.productId];
  }

  // Set miscProductIds.value once
  miscProductIds.value = updatedMiscProductIds;
};

const courses = reactive({
  additionalCredit: false,
  courseIds: [],
});

const misc = reactive({
  explicitlyRefuseAddOns: false,
});

const sessionTravelDetails = computed(() => {
  return selectedSession.value?.session_travel_details ?? [];
});
const arrivalDetails = computed(() => {
  const details = [...sessionTravelDetails.value];
  const sortedAsc = details.sort((a, b) => Number(new Date(a.arrival_date)) - Number(new Date(b.arrival_date)));
  return sortedAsc.length ? sortedAsc[0] : {};
});
const departureDetails = computed(() => {
  const details = [...sessionTravelDetails.value];
  const sortedDesc = details.sort((a, b) => Number(new Date(b.departure_date)) - Number(new Date(a.departure_date)));
  return sortedDesc.length ? sortedDesc[0] : {};
});

const additionalCreditFee = computed(() => {
  let fee = 0;
  if (courses.additionalCredit) {
    const oneCreditProduct = selectedSession.value?.additional_session_fees.find(
      (item) => item.product_id === ONE_CREDIT_PRODUCTS.id,
    );
    const threeCreditProduct = selectedSession.value?.additional_session_fees.find(
      (item) => item.product_id === THREE_CREDIT_PRODUCTS.id,
    );

    const oneCreditPrice = oneCreditProduct?.price_in_cents ?? 0;
    const threeCreditPrice = threeCreditProduct?.price_in_cents ?? 0;

    const finalCredits = numberCredits.value - selectedSession.value.academic_max_credits;
    const quotient = Math.floor(finalCredits / 3);
    const remainder = finalCredits % 3;

    fee = quotient * threeCreditPrice + remainder * oneCreditPrice;
  }
  return fee;
});

const preLoadInternshipCredits = async () => {
  credits.loading = true;
  const promises = TRANSCRIPT_SERVICE_IDS.map((id) => miscProductsService.getById(id));
  return Promise.allSettled(promises)
    .then((promises) => {
      promises.forEach((fulfilledPromiseObject) => {
        const response = fulfilledPromiseObject?.value;
        const productId = response?.data?.data?.id ?? "";
        const productPrices = response?.data?.data?.product_prices ?? [];
        credits.prices[productId] = productPrices.reduce((acc, p) => {
          return acc + p.price_in_cents;
        }, 0);
      });
    })
    .finally(() => {
      credits.loading = false;
    });
};

const loadInternships = async ({
  pagination = { itemsPerPage: 10, currentPage: 1 },
  q = "",
  major = "",
  career_area = "",
  skip = 0,
}) => {
  internship.loading = true;
  const params = {
    limit: pagination.itemsPerPage,
    skip: skip,
    status: "Active",
    q: q,
    durations: internshipFilterDurations.value,
    terms: internshipFilterTerms.value,
    city_ids: internshipFilterCities.value,
    major: major,
    career_area: career_area,
  };

  const response = await internshipService.getInternships(params);
  const data = response?.data?.data ?? {};
  internship.internshipItems = {
    items: data.items,
    count: data.count,
    end: data.end,
    start: data.start,
  };
  internship.loading = false;
};

const contracts = ref([]);
watch(
  () => ope8Id.value,
  async (newOPEId, oldOPEId) => {
    if (newOPEId !== oldOPEId) {
      if (!newOPEId) {
        contracts.value = [];
      } else {
        const foundContracts = await enrollmentService.loadContracts(newOPEId);
        contracts.value = Object.assign([], foundContracts);
      }
    }
  },
  { immediate: true, deep: true },
);
provide("contracts", readonly(contracts));

const preLoadSessionProducts = async () => {
  if (savedOrder.value?.id && !step.value) {
    setOrderStep();
  }
  setCourseCredits();
  if (showInternship()) {
    await loadInternships({
      city_ids: internshipFilterCities.value,
      terms: internshipFilterTerms.value,
      durations: internshipFilterDurations.value,
    });
  }
};

const loadPreselectedUniversity = async () => {
  const institutionIdToFetch = homeInstitutionId.value ?? homeInstitutionIdFromProfile.value;
  if (!institutionIdToFetch) {
    program.loadedUniversities = [];
    return;
  }

  try {
    const response = await entitiesService.getEntityById(institutionIdToFetch);
    const selectedInstitution = response?.data?.data?.items;
    if (selectedInstitution) {
      program.loadedUniversities = [selectedInstitution];
      homeInstitutionId.value = selectedInstitution.id;
    }
  } catch {
    program.loadedUniversities = [];
  }
};

const hasValidLearnerType = ref(false);
const hasValidHomeInstitution = ref(false);

provide("program", {
  program: readonly(program),
  selectedSession,
  selectedSessionId,
  loadedSessions,
  hasValidLearnerType,
  hasValidHomeInstitution,
  arrivalDetails: readonly(arrivalDetails),
  departureDetails: readonly(departureDetails),
  profileData: readonly(store.state?.profileData),
  setLoadedUniversities: (universities = []) => {
    program.loadedUniversities = universities;
  },
  homeInstitutionId,
  learnerTypeId,
  defaultLearnerTypeId,
  programPageId,
});

provide("housing", {
  housing: readonly(housing),
  setRoomIds: (ids) => {
    roomIds.value = ids;
  },
  setQuestionnaire: (questionnaire) => {
    housing.questionnaire = questionnaire.value;
  },
  roomPrices: computed(() => {
    const productListings = configuratorOptions.value?.rooms ?? [];
    return Object.fromEntries(productListings?.map((room) => [room.productId, room.studentTotalPriceInCents]));
  }),
});

provide("excursionRules", excursionRules);

const getInternships = computed(() => {
  return internship.internshipItems.items.filter((item) => internshipIds.value.includes(item.id));
});
provide("credits", {
  credits: readonly(credits),
  internships: getInternships,
  miscProductIds,
  setCreditsValue,
});

provide("internship", {
  internship: readonly(internship),
  selectedInternshipIds: internshipIds,
  addInternship: (id) => {
    if (!internshipIds.value.includes(id)) {
      internshipIds.value = [...internshipIds.value, id];
    }
  },
  removeInternship: (id) => {
    internshipIds.value = internshipIds.value.filter((_id) => id !== _id);
  },
  loadInternships,
});

provide("courses", {
  courses: readonly(courses),
  selectedCredit: readonly(numberCredits),
  additionalCreditFee: readonly(additionalCreditFee),
  setCredits: (value) => {
    numberCredits.value = value;
  },
  setAdditionalCredit: (value) => {
    courses.additionalCredit = value;
  },
});

provide("misc", {
  misc: readonly(misc),
  includeAddOn: (addon) => {
    misc.explicitlyRefuseAddOns = false;
    miscProductIds.value = [...miscProductIds.value, addon];
  },
  refuseAddOns: () => {
    misc.explicitlyRefuseAddOns = true;
  },
});

const programStepIsComplete = () => {
  return (
    (hasValidLearnerType.value &&
      Boolean(selectedSessionId.value) &&
      learnerTypeId.value &&
      homeInstitutionIsSelectedOrNotRequired()) ||
    Boolean(orderApplicationId.value)
  );
};

const homeInstitutionIsSelectedOrNotRequired = () => {
  if (learnerTypeIdsThatRequireSchoolSelection.includes(learnerTypeId.value)) {
    return Boolean(homeInstitutionId.value) && hasValidHomeInstitution.value;
  }
  return true;
};

const hasCreditsMiscProducts = (productIds) => {
  const creditProductIds = new Set(TRANSCRIPT_SERVICE_IDS);
  return new Set(productIds).intersection(creditProductIds).size > 0;
};

const creditsStepIsComplete = () => {
  return (
    internshipStepIsComplete() &&
    (Boolean(credits.option) || hasCreditsMiscProducts(miscProductIds.value) || Boolean(route.params?.applicationId))
  );
};

const housingQuestionnaireIsAssigned = computed(() => {
  if (!hasMyStudentsToDosEnabled.value) return true;
  return Boolean(application.value?.sessionTodoAssignments?.value?.[TODOS_IDS.housingQuestionnaire]);
});

const housingStepIsComplete = () => {
  const hasDefaultRoom = Boolean(selectedSession.value?.default_room?.id);
  const hasSelectedRoom = Boolean(roomIds.value.length > 0);
  const hasQuestionnaire = Boolean(housing.questionnaire);
  const questionnaireSatisfied = !housingQuestionnaireIsAssigned.value || hasQuestionnaire;

  if (hasDefaultRoom) {
    return hasSelectedRoom && questionnaireSatisfied;
  }
  if (!hasSelectedRoom) {
    return true;
  }
  return questionnaireSatisfied;
};

const internshipStepIsComplete = () => Boolean(internshipIds.value.length >= internship.requiredItems);

const coursesStepIsComplete = () => Boolean(programStepIsComplete());

const showCredits = () => showInternship() && homeInstitutionId.value !== SALESFORCE_UMASS_ID;
const showCourses = () => !_.isEmpty(selectedSession.value?.class_filter_criteria);
const showHousing = () => !_.isEmpty(selectedSession.value?.session_units);
const showExcursions = () =>
  !_.isEmpty(selectedSession.value?.session_events) ||
  !_.isEmpty(selectedSession.value?.session_event_selection_rules);

const showInternship = () =>
  !_.isEmpty(selectedSession.value?.internship_filter_criteria?.durations) ||
  !_.isEmpty(selectedSession.value?.internship_filter_criteria?.cities) ||
  !_.isEmpty(selectedSession.value?.internship_filter_criteria?.terms);

const internshipStepTitle = computed(() => getInternshipSectionTitle(selectedSession.value));

const steps = computed(() => {
  const availableSteps = [
    {
      slug: "program",
      title: "Program",
      showStep: true,
      isSelectable: () => true,
      isComplete: programStepIsComplete,
      component: ProgramStep,
    },
    {
      slug: "loader",
      title: "loading",
      showStep: _.isEmpty(selectedSession.value),
      isSelectable: () => false,
      isComplete: () => false,
    },
    {
      slug: "loader2",
      title: "loading",
      showStep: _.isEmpty(selectedSession.value),
      isSelectable: () => false,
      isComplete: () => false,
    },
    {
      slug: "courses",
      title: "Courses",
      showStep: showCourses(),
      isSelectable: programStepIsComplete,
      isComplete: coursesStepIsComplete,
      component: CoursesStep,
    },
    {
      slug: "internship",
      title: internshipStepTitle.value,
      showStep: showInternship(),
      isSelectable: programStepIsComplete,
      isComplete: internshipStepIsComplete,
      component: InternshipStep,
    },
    {
      slug: "credits",
      title: "Credits",
      showStep: showCredits(),
      isSelectable: internshipStepIsComplete,
      isComplete: creditsStepIsComplete,
      component: CreditsStep,
    },
    {
      slug: "housing",
      title: "Housing",
      showStep: showHousing(),
      isSelectable: programStepIsComplete,
      isComplete: housingStepIsComplete,
      component: HousingStep,
    },
    {
      slug: "excursions",
      title: showNewExcursionConfiguratorDesign.value ? "Curated Experiences" : "Excursions",
      showStep: showExcursions(),
      isSelectable: () => previousStepIsCompleted("excursions"),
      isComplete: excursionsStepIsComplete,
      component: showNewExcursionConfiguratorDesign.value ? ExcursionConfigurator : ExcursionsStep,
    },
  ];

  return availableSteps.filter((step) => step.showStep || (route.params.orderId && route.params.slug === step.slug));
});

const getFirstIncompleteOrLastStep = computed(() => {
  const step = steps.value[steps.value.length - 1];
  const incompleteStep = steps.value.find((s) => !s.isComplete());
  return incompleteStep ?? step;
});

const currentStep = computed(() => {
  const slug = step.value;
  if (!slug) {
    if (route.name === "new-application") {
      return steps.value[0];
    }
    return getFirstIncompleteOrLastStep.value;
  }
  if (slug === "steps-complete") {
    return {
      slug: "steps-complete",
      isSelectable: () => true,
      component: StepsCompleted,
    };
  } else {
    return steps.value.find((step) => step.slug === slug) || steps.value[0];
  }
});

const setCurrentStep = (slug) => {
  const newStepIndex = steps.value.findIndex((step) => step.slug === slug);
  const newStep = steps.value[newStepIndex];
  if (newStep.isSelectable()) {
    const to = {
      params: { ...route.params, step: newStep.slug },
      query: route.query,
    };
    router.push(to);
    return;
  }
  // if desired step is not yet selectable, go to the first
  // selectable step in the sequence
  setCurrentStep(steps.value[newStepIndex - 1].slug);
};

const previousStepIsCompleted = (slug) => {
  const stepIndex = steps.value.findIndex((step) => step.slug === slug);

  if (stepIndex <= 0) {
    return true;
  }

  const previousStep = steps.value[stepIndex - 1];
  return previousStep.isComplete();
};

const firstIncompleteStep = computed(() => {
  return steps.value.find((step) => !step.isComplete());
});

const goToNextStep = () => {
  const index = steps.value.findIndex((step) => step.slug === currentStep.value.slug);
  if (index === steps.value.length - 1) {
    //The returned index is the last element in the steps
    goToFinalPage();
  } else {
    //The returned index is not the last element in the steps
    const nextStep = steps.value[index + 1] ?? currentStep.value;
    if (nextStep.isSelectable()) {
      setCurrentStep(nextStep.slug);
    } else {
      setCurrentStep(firstIncompleteStep.value.slug);
    }
  }
};

const goToPreviousStep = () => {
  const index = steps.value.findIndex((step) => step.slug === currentStep.value.slug);
  const previousStep = index > 0 ? steps.value[index - 1] : currentStep.value;
  if (previousStep.isSelectable()) {
    setCurrentStep(previousStep.slug);
  }
};

const allStepsCompleted = computed(() => steps.value.every((step) => Boolean(step?.isComplete())));

const goToFinalPage = () => {
  if (allStepsCompleted.value) {
    router.push({
      params: { ...route.params, step: "steps-complete" },
      query: route.query,
    });
  } else {
    const firstIncomplete = steps.value.find((step) => !step.isComplete()) ?? steps.value[0];
    setCurrentStep(firstIncomplete.slug);
  }
};

provide("steps", {
  steps: readonly(steps),
  currentStep: readonly(currentStep),
  setCurrentStep,
  goToNextStep,
  goToPreviousStep,
});

const updateProfileColleges = async () => {
  if (!application.value) {
    // No application present
    return;
  }
  await application.value.updateProfileColleges(learnerTypeId.value, program.school);
};

provide("updates", {
  updateProfileColleges,
});

const hidePrices = computed(
  () =>
    currentStep.value.slug === steps.value[0]?.slug ||
    application.value?.programPage?.value?.display_starting_price === false,
);

const unsavedOrder = computed(() => {
  const order = {
    program_name: selectedSession.value?.name,
    orderId: savedOrder.value?.id,
    program_page_id: programPageId.value,
    session_id: selectedSessionId.value,
    room_ids: roomIds.value,
    event_occurrence_ids: selectedExcursionIds.value,
    internship_ids: internshipIds.value,
    credits: numberCredits.value,
    misc_product_ids: miscProductIds.value,
    ope_id: ope8Id.value,
    home_institution_id: homeInstitutionId.value,
    status: savedOrder.value?.status ?? "Draft",
    application_fee_payment_id: savedOrder.value?.application_fee_payment_id,
    participant_id: participantId.value,
    // special_pricing_override_entity_id is frozen once the order exists: this object also feeds
    // updateOrder, and a student may create on one storefront and finish on another, so for an
    // existing order we forward the PERSISTED value and never re-derive it from the current domain
    // (only the fulfillment/CMS flow may change it). Before the order exists the upsert path creates
    // (not updates), so there is nothing to protect — the current storefront's override (flag-gated)
    // is both what the pre-create quote should show and what gets persisted at create.
    special_pricing_override_entity_id: savedOrder.value?.id
      ? savedOrder.value.special_pricing_override_entity_id
      : specialPricingOverrideEntityId(),
  };

  // just making sure that no future unwrapped refs sneak their way in and cause problems
  // downstream
  return _.mapValues(order, toValue);
});

const setCourseCredits = () => {
  if (
    selectedSession.value?.academic_min_credits === selectedSession.value?.academic_max_credits &&
    !selectedSession.value?.academic_additional_courses_allowed
  ) {
    numberCredits.value = selectedSession.value?.academic_min_credits ?? 0;
  }
};

const internshipFilterTerms = computed(() => {
  return selectedSession.value?.internship_filter_criteria?.terms?.map((term) => term.id) ?? [];
});
const internshipFilterCities = computed(() => {
  return selectedSession.value?.internship_filter_criteria?.cities?.map((city) => city.city_id) ?? [];
});
const internshipFilterDurations = computed(() => {
  return selectedSession.value?.internship_filter_criteria?.durations ?? [];
});

watch(homeInstitutionId, (newValue, oldValue) => {
  if (_.isEqual(newValue, oldValue)) {
    return;
  }
  loadConfiguratorOptions();
});

provide("configuratorOptions", {
  configuratorOptions: configuratorOptions,
  getSelectedRooms: () =>
    configuratorOptions.value.rooms.filter((room) => roomIds.value.find((id) => id === room.productId)),
});

watch(
  currentStep,
  () => {
    if (!currentStep.value?.isSelectable()) {
      setCurrentStep((steps.value.find((step) => step?.isSelectable()) ?? steps.value[0])?.slug);
    }
  },
  { immediate: true },
);

watch(selectedSession, (newValue, oldValue) => {
  if (Boolean(newValue) && !_.isEqual(newValue?.id, oldValue?.id)) {
    loadConfiguratorOptions();
  }
});

const { createSubmissionDataForFormIo } = useProgramSelectionData();
const { sendNotifications } = useNotifications();

const oktaId = computed(() => {
  return store.state.currentUser?.participantId ?? "";
});
const profileData = computed(() => {
  return store.state.profileData;
});

const saveForLater = async () => {
  if (!canRegisterForPrograms.value) {
    toast.open({
      message: "Application creation is not allowed for your account type.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    return;
  }

  disabledSaveForLater.value = true;
  _loading.value = true;
  setLoadingMessage("Loading My Experiences. Please wait.");
  try {
    application.value?.stashUnsavedHousingQuestionnaire(housing.questionnaire, housing.questionnaireId);
    application.value?.stashUnsavedOrder(unsavedOrder);
    await application.value?.saveForLater();
    await router.push({
      name: "experiences",
    });
  } finally {
    _loading.value = false;
    setLoadingMessage("Loading registration.  Please wait.");
  }
};

const loadStudentType = () => {
  const studentType = store.state.userData?.data?.student_type;
  const studentTypeId = profileData.value?.learner_type_id;
  const university = currentUser.value?.university;

  if (studentType && studentTypeId) program.learnerType = { value: studentType, id: studentTypeId };

  if (university) {
    program.school = {
      name: university.label,
      value: university.value,
      ope_id: university.value,
    };
  }
};

const loadHousingQuestionnaire = (newHousingQuestionnaire) => {
  housing.questionnaire = newHousingQuestionnaire?.submission_data
    ? structuredClone(newHousingQuestionnaire?.submission_data)
    : undefined;
  housing.questionnaireId = newHousingQuestionnaire?.id;
};

watch(savedHousingQuestionnaire, (newHousingQuestionnaire) => loadHousingQuestionnaire(newHousingQuestionnaire), {
  immediate: true,
});

const loadNoCreditOption = () => {
  if (route.name === "modify-application" && !hasCreditsMiscProducts(savedOrder.value?.misc_product_ids ?? [])) {
    credits.option = "noCredit";
  }
};

const loadSelectionsFromSavedOrder = async () => {
  const orderExists = Boolean(savedOrder.value);

  if (!orderExists) {
    if (!currentStep.value.isSelectable()) {
      setCurrentStep("program");
    }
    return;
  }

  loadNoCreditOption();
  loadStudentType();

  await router.replace({
    query: {
      ...route.query,
      ...buildOrderQueryParams(savedOrder.value),
    },
  });
};

const setOrderStep = () => {
  // Set current step
  const lastCompletedIndex = steps.value.findLastIndex((step) => step.isComplete());
  if (steps.value.length === 1) {
    setCurrentStep(steps.value[0].slug);
  } else if (lastCompletedIndex === steps.value.length - 1) {
    // If all the steps are completed
    // current step will be the second selectable step
    const secondStep = steps.value.find((step) => step?.isSelectable() && step?.slug !== "program");
    setCurrentStep(secondStep.slug);
  } else {
    const firstIncompleteStep = steps.value[lastCompletedIndex + 1];
    setCurrentStep(firstIncompleteStep.slug);
  }
};

const validateApplication = async () => {
  if (store.getters.isLoggedIn && !unsavedOrder.value.orderId && Boolean(selectedSessionId.value)) {
    const { studentApplications } = store.state;
    const exists = await applicationAlreadyExists({
      programSession: selectedSession.value,
      studentApplications: studentApplications,
      skipRequest: true,
    });
    showApplicationTakenModal.value = exists;
    duplicateApplication.value = exists;
  }
};

provide("application", {
  duplicateApplication: readonly(duplicateApplication),
  validateApplication,
  application,
});

provide("currentOrder", {
  order: unsavedOrder,
});
provide("programSelectionId", programSelectionId);

watch(
  selectedSessionId,
  (newSession) => {
    if (newSession) {
      loadConfiguratorOptions();
      preLoadSessionProducts();
    }
  },
  { immediate: true },
);

watch(() => showInternship(), loadInternships);

watch(
  savedOrder,
  (incomingOrder) => {
    if (incomingOrder) {
      loadSelectionsFromSavedOrder();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  _loading.value = true;

  // Get profile data if we have a profile id and store is empty
  if (oktaId.value && _.isEmpty(profileData.value)) {
    await store.dispatch("getProfile", oktaId.value);
  }

  await Promise.allSettled([loadPreselectedUniversity(), preLoadInternshipCredits()]);
  await validateApplication();
  await fetchAndSetSelectedSchool(homeInstitutionIdFromProfile.value);
  _loading.value = false;
});
</script>
